import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { firestoreService } from '@/utils/firestoreService'

export interface EmailSettings {
  defaultSignature: string
  defaultTemplate: string | null
  defaultFrom: string
  autoBcc: boolean
  trackOpens: boolean
  replyTo: string
}

export type NotificationFrequency = 'immediate' | 'daily' | 'weekly'

export interface NotificationSettings {
  emailNotifications: boolean
  pushNotifications: boolean
  smsNotifications: boolean
  frequency: NotificationFrequency
  emailDefaultTo?: string | null
  events: {
    transactionFinalized: {
      email: boolean
      push: boolean
      sms: boolean
      emailTo?: string | null
    }
  }
}

export interface CrmSettings {
  defaultPriority: string | null
  reminderTime: string | null
  leadScoreThreshold: number
  autoAssignTasks: boolean
  trackActivities: boolean
  activityTypes: string[]
}

interface CommoditySettings {
  categories: string[]
}

export interface VehicleFieldConfig {
  key: string
  label: string
  enabled: boolean
}

export type RuleOperator = 'AND' | 'OR' | 'IF_THEN'
export type RuleCondition = 'HAS_VALUE' | 'IS_EMPTY' | 'EQUALS' | 'CONTAINS'

export interface VehicleValidationRule {
  id: string
  name: string
  description: string
  type: 'required' | 'conditional' | 'group' | 'business'
  operator?: RuleOperator
  conditions?: RuleCondition[]
  fields: string[]
  message: string
  enabled: boolean
}

export interface VehicleRuleGroup {
  id: string
  name: string
  description: string
  operator: 'AND' | 'OR'
  rules: string[] // Rule IDs
  enabled: boolean
}

interface TransactionSettings {
  defaultCurrency: string | null
  numberFormat: string
  taxRate: number
  defaultTemplate: string | null
  vehicleFieldConfig: VehicleFieldConfig[]
  vehicleValidationRules: VehicleValidationRule[]
  vehicleRuleGroups: VehicleRuleGroup[]
  autoNumber: boolean
  requireApproval: boolean
  approvalThreshold: number
  requirePhoneNumber: boolean
}

interface Settings {
  email: EmailSettings
  crm: CrmSettings
  commodity: CommoditySettings
  transactions: TransactionSettings
  notifications: NotificationSettings
  idDocuments: IDDocumentSettings
  phone: PhoneSettings
  company: CompanySettings
}

export interface IDDocumentSettings {
  requireBackPhoto: boolean
}

export interface PhoneSettings {
  callUrlType: 'default' | 'custom'
  callUrlTemplate: string
  messageUrlTemplate: string
}

export interface CompanySettings {
  name: string
  email: string
  website: string
  logoUrl: string | null
  address: string
  phone: string
  reviewUrl: string | null
}

const defaultSettings: Settings = {
  email: {
    defaultSignature: '',
    defaultTemplate: null,
    defaultFrom: '',
    autoBcc: false,
    trackOpens: true,
    replyTo: '',
  },
  crm: {
    defaultPriority: null,
    reminderTime: null,
    leadScoreThreshold: 50,
    autoAssignTasks: true,
    trackActivities: true,
    activityTypes: [],
  },
  commodity: {
    categories: ['Ferrous', 'Non-ferrous', 'Electronic', 'Precious Metal', 'Other'],
  },
  transactions: {
    defaultCurrency: null,
    numberFormat: 'TRX-{year}-{number}',
    taxRate: 0,
    defaultTemplate: null,
    vehicleFieldConfig: [],
    vehicleValidationRules: [],
    vehicleRuleGroups: [],
    autoNumber: true,
    requireApproval: false,
    approvalThreshold: 1000,
    requirePhoneNumber: true,
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    frequency: 'immediate',
    emailDefaultTo: null,
    events: {
      transactionFinalized: { email: true, push: false, sms: false, emailTo: null },
    },
  },
  idDocuments: {
    requireBackPhoto: false,
  },
  phone: {
    callUrlType: 'default',
    callUrlTemplate: 'openphone://dial?number={{phone}}',
    messageUrlTemplate: 'sms:{{phone}}',
  },
  company: {
    name: '',
    email: '',
    website: '',
    logoUrl: null,
    address: '',
    phone: '',
    reviewUrl: null,
  },
}

export const useSettingsStore = defineStore(
  'settings',
  () => {
    const settings = ref<Settings>(defaultSettings)
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    // Getters
    const emailSettings = computed(() => settings.value.email)
    const crmSettings = computed(() => settings.value.crm)
    const commoditySettings = computed(() => settings.value.commodity)
    const transactionSettings = computed(() => settings.value.transactions)
    const notificationSettings = computed(() => settings.value.notifications)
    const idDocumentSettings = computed(() => settings.value.idDocuments)
    const phoneSettings = computed(() => settings.value.phone)
    const companySettings = computed(() => settings.value.company)

    // Actions
    async function loadSettings() {
      isLoading.value = true
      error.value = null

      try {
        // Try loading from individual documents first (new structure)
        const [
          emailData,
          crmData,
          commodityData,
          transactionsData,
          notificationsData,
          idDocumentsData,
          phoneData,
          companyData,
        ] = await Promise.all([
          firestoreService.get<EmailSettings>('settings', 'email'),
          firestoreService.get<CrmSettings>('settings', 'crm'),
          firestoreService.get<CommoditySettings>('settings', 'commodity'),
          firestoreService.get<TransactionSettings>('settings', 'transactions'),
          firestoreService.get<NotificationSettings>('settings', 'notifications'),
          firestoreService.get<IDDocumentSettings>('settings', 'idDocuments'),
          firestoreService.get<PhoneSettings>('settings', 'phone'),
          firestoreService.get<CompanySettings>('settings', 'company'),
        ])

        // Check if we have at least one individual document
        const hasIndividualDocs =
          emailData ||
          crmData ||
          commodityData ||
          transactionsData ||
          notificationsData ||
          idDocumentsData ||
          phoneData ||
          companyData

        if (hasIndividualDocs) {
          // Merge individual documents with defaults
          const merged: Settings = {
            email: { ...defaultSettings.email, ...(emailData || {}) },
            crm: { ...defaultSettings.crm, ...(crmData || {}) },
            commodity: { ...defaultSettings.commodity, ...(commodityData || {}) },
            transactions: {
              ...defaultSettings.transactions,
              ...(transactionsData || {}),
            },
            notifications: {
              ...defaultSettings.notifications,
              ...(notificationsData || {}),
            },
            idDocuments: {
              ...defaultSettings.idDocuments,
              ...(idDocumentsData || {}),
            },
            phone: {
              ...defaultSettings.phone,
              ...(phoneData || {}),
            },
            company: {
              ...defaultSettings.company,
              ...(companyData || {}),
            },
          }

          // Backward-compat: migrate boolean event flags to channel object shape
          const evt: any = merged.notifications.events
          if (evt && typeof evt.transactionFinalized === 'boolean') {
            evt.transactionFinalized = {
              email: Boolean(evt.transactionFinalized),
              push: false,
              sms: false,
            }
          }

          settings.value = merged
        } else {
          // Fall back to global document for backward compatibility
          const globalSettings = await firestoreService.get<Partial<Settings>>('settings', 'global')
          if (globalSettings) {
            // Merge with defaults to ensure newly added keys exist
            const merged: Settings = {
              ...defaultSettings,
              ...(globalSettings as Settings),
              email: { ...defaultSettings.email, ...(globalSettings.email || {}) },
              crm: { ...defaultSettings.crm, ...(globalSettings.crm || {}) },
              commodity: {
                ...defaultSettings.commodity,
                ...(globalSettings.commodity || {}),
              },
              transactions: {
                ...defaultSettings.transactions,
                ...(globalSettings.transactions || {}),
              },
              notifications: {
                ...defaultSettings.notifications,
                ...(globalSettings.notifications || {}),
              },
              idDocuments: {
                ...defaultSettings.idDocuments,
                ...(globalSettings.idDocuments || {}),
              },
              phone: {
                ...defaultSettings.phone,
                ...(globalSettings.phone || {}),
              },
              company: {
                ...defaultSettings.company,
                ...(globalSettings.company || {}),
              },
            }

            // Backward-compat: migrate boolean event flags to channel object shape
            const evt: any = (merged.notifications as any).events
            if (evt && typeof evt.transactionFinalized === 'boolean') {
              evt.transactionFinalized = {
                email: Boolean(evt.transactionFinalized),
                push: false,
                sms: false,
              }
            }

            settings.value = merged
          } else {
            // Initialize settings if they don't exist
            // Create individual documents with default values
            await Promise.all([
              firestoreService.set('settings', { id: 'email', ...defaultSettings.email }),
              firestoreService.set('settings', { id: 'crm', ...defaultSettings.crm }),
              firestoreService.set('settings', {
                id: 'commodity',
                ...defaultSettings.commodity,
              }),
              firestoreService.set('settings', {
                id: 'transactions',
                ...defaultSettings.transactions,
              }),
              firestoreService.set('settings', {
                id: 'notifications',
                ...defaultSettings.notifications,
              }),
              firestoreService.set('settings', {
                id: 'idDocuments',
                ...defaultSettings.idDocuments,
              }),
              firestoreService.set('settings', { id: 'phone', ...defaultSettings.phone }),
              firestoreService.set('settings', {
                id: 'company',
                ...defaultSettings.company,
              }),
            ])
            settings.value = defaultSettings
          }
        }
      } catch (err) {
        error.value = 'Failed to load settings'
        console.error('Failed to load settings:', err)
      } finally {
        isLoading.value = false
      }
    }

    async function updateEmailSettings(newSettings: Partial<EmailSettings>) {
      settings.value.email = { ...settings.value.email, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'email',
          ...settings.value.email,
        })
      } catch (err) {
        error.value = 'Failed to save email settings'
        console.error('Failed to save email settings:', err)
        throw err
      }
    }

    async function updateCrmSettings(newSettings: Partial<CrmSettings>) {
      settings.value.crm = { ...settings.value.crm, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'crm',
          ...settings.value.crm,
        })
      } catch (err) {
        error.value = 'Failed to save CRM settings'
        console.error('Failed to save CRM settings:', err)
        throw err
      }
    }

    async function updateCommoditySettings(newSettings: Partial<CommoditySettings>) {
      settings.value.commodity = { ...settings.value.commodity, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'commodity',
          ...settings.value.commodity,
        })
      } catch (err) {
        error.value = 'Failed to save commodity settings'
        console.error('Failed to save commodity settings:', err)
        throw err
      }
    }

    async function updateTransactionSettings(newSettings: Partial<TransactionSettings>) {
      settings.value.transactions = { ...settings.value.transactions, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'transactions',
          ...settings.value.transactions,
        })
      } catch (err) {
        error.value = 'Failed to save transaction settings'
        console.error('Failed to save transaction settings:', err)
        throw err
      }
    }

    async function updateNotificationSettings(newSettings: Partial<NotificationSettings>) {
      settings.value.notifications = { ...settings.value.notifications, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'notifications',
          ...settings.value.notifications,
        })
      } catch (err) {
        error.value = 'Failed to save notification settings'
        console.error('Failed to save notification settings:', err)
        throw err
      }
    }

    async function updateIDDocumentSettings(newSettings: Partial<IDDocumentSettings>) {
      settings.value.idDocuments = { ...settings.value.idDocuments, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'idDocuments',
          ...settings.value.idDocuments,
        })
      } catch (err) {
        error.value = 'Failed to save ID document settings'
        console.error('Failed to save ID document settings:', err)
        throw err
      }
    }

    async function updatePhoneSettings(newSettings: Partial<PhoneSettings>) {
      settings.value.phone = { ...settings.value.phone, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'phone',
          ...settings.value.phone,
        })
      } catch (err) {
        error.value = 'Failed to save phone settings'
        console.error('Failed to save phone settings:', err)
        throw err
      }
    }

    async function updateCompanySettings(newSettings: Partial<CompanySettings>) {
      settings.value.company = { ...settings.value.company, ...newSettings }
      try {
        await firestoreService.set('settings', {
          id: 'company',
          ...settings.value.company,
        })
      } catch (err) {
        error.value = 'Failed to save company settings'
        console.error('Failed to save company settings:', err)
        throw err
      }
    }

    return {
      settings,
      emailSettings,
      crmSettings,
      commoditySettings,
      transactionSettings,
      notificationSettings,
      idDocumentSettings,
      phoneSettings,
      companySettings,
      isLoading,
      error,
      loadSettings,
      updateEmailSettings,
      updateCrmSettings,
      updateCommoditySettings,
      updateTransactionSettings,
      updateNotificationSettings,
      updateIDDocumentSettings,
      updatePhoneSettings,
      updateCompanySettings,
    }
  },
  {
    persist: {
      storage: localStorage,
      paths: ['settings'],
    } as any,
  },
)
